import { EntityAttachment, EntityAttachmentType } from '@thewhite/react-attachments/lib/models';
import * as t from 'io-ts';
import { DateService } from 'ws-react-global-module/lib/services';
import { DateType } from 'ws-react-global-module/lib/types';
import { IPublicStatusComplaintItem } from 'ws-voice-infinity-global-module/lib/interfaces';
import { ComplaintFlagsEntry, ComplaintFlagsEntryType, ComplaintSubject, ComplaintSubjectType, DictionaryEntry, DictionaryEntryType, GeoPoint, GeoPointType, Rating, RatingType, UserDescriptorEntry, UserDescriptorEntryType } from 'ws-voice-infinity-global-module/lib/models';
import PUBLIC_STATUS_COMPLAINT, { PublicStatusComplaintType } from '../enums/publicStatusComplaint';

export const ComplaintInternalType = t.interface({
    address: t.union([t.string, t.null]),
    attachments: t.array(EntityAttachmentType),
    author: t.union([UserDescriptorEntryType, t.null]),
    content: t.union([t.string, t.null]),
    currentActiveTimerId: t.union([t.string, t.null]),
    currentExecutor: t.union([DictionaryEntryType, t.null]),
    id: t.string,
    lastResponseDate: t.union([t.string, t.number, t.null]),
    municipality: t.union([DictionaryEntryType, t.null]),
    number: t.union([t.number, t.null]),
    locality: t.union([DictionaryEntryType, t.null, t.undefined]),
    point: t.union([GeoPointType, t.null]),
    publicStatus: t.union([PublicStatusComplaintType, t.null]),
    registrationDate: t.union([t.string, t.number, t.null]),
    subject: t.union([ComplaintSubjectType, t.null]),
    submitDate: t.union([t.string, t.number, t.null]),
    rating: t.union([RatingType, t.null, t.undefined]),

    executors: t.array(t.string),
    expiredStatus: t.union([t.string, t.null]),
    flags: ComplaintFlagsEntryType,
    internalStatus: t.union([t.string, t.null]),
    messageStationId: t.union([t.string, t.null, t.undefined]),
    operator: t.union([DictionaryEntryType, t.null]),
    responseNotificationAddress: t.union([t.string, t.null]),
    sedNumber: t.union([t.string, t.null]),
    tags: t.array(t.string),

    meetingDate: t.union([DateType, t.null]),
    videoChatId: t.union([t.string, t.null]),
    meetingPerson: t.union([
        t.interface({
            name: t.union([t.string, t.null]),
            post: t.union([t.string, t.null]),
        }),
        t.null,
    ]),
});

export interface ComplaintInternalDTO extends t.TypeOf<typeof ComplaintInternalType> {
}

export class ComplaintInternal {
    address: string | null;
    attachmentIds: string[];
    attachments: EntityAttachment[];
    author: UserDescriptorEntry | null;
    content: string | null;
    currentActiveTimerId: string | null;
    currentExecutor: DictionaryEntry | null;
    id: string;
    lastResponseDate: Date | null;
    municipality: DictionaryEntry | null;
    number: number | null;
    locality?: DictionaryEntry | null;
    point: GeoPoint | null;
    publicStatus: IPublicStatusComplaintItem | null;
    registrationDate: Date | null;
    subject: ComplaintSubject | null;
    submitDate: Date | null;
    flags: ComplaintFlagsEntry | null;
    responseNotificationAddress: string | null;
    rating: Rating | null;

    executors: string[];
    expiredStatus: string | null;
    internalStatus: string | null;
    messageStationId?: string | null;
    operator: DictionaryEntry | null;
    sedNumber: string | null;
    tags: string[];

    meetingDate: Date | null;
    videoChatId: string | null;
    meetingPerson:
        {
            name: string | null;
            post: string | null;
        }
        | null;

    constructor(params: ComplaintInternalDTO) {
        this.address = params.address;
        this.attachmentIds = params.attachments && params.attachments.length ? params.attachments.map((el: any) => el.nodeId) : [];
        this.attachments = params.attachments ? params.attachments.map(attachment => new EntityAttachment(attachment)) : [];
        this.author = params.author ? new UserDescriptorEntry(params.author) : null;
        this.content = params.content;
        this.currentActiveTimerId = params.currentActiveTimerId;
        this.currentExecutor = params.currentExecutor ? new DictionaryEntry(params.currentExecutor) : null;
        this.id = params.id;
        this.lastResponseDate = params.lastResponseDate ? new Date(params.lastResponseDate) : null;
        this.municipality = params.municipality ? new DictionaryEntry(params.municipality) : null;
        this.number = params.number;
        this.locality = params.locality ? new DictionaryEntry(params.locality) : null;
        this.point = params.point ? new GeoPoint(params.point) : null;
        this.publicStatus = params.publicStatus ? PUBLIC_STATUS_COMPLAINT[params.publicStatus] : PUBLIC_STATUS_COMPLAINT.DRAFT;
        this.registrationDate = params.registrationDate ? new Date(params.registrationDate) : null;
        this.subject = params.subject ? new ComplaintSubject(params.subject) : null;
        this.submitDate = params.submitDate ? new Date(params.submitDate) : null;
        this.flags = null;
        this.responseNotificationAddress = null;
        this.rating = params.rating ? new Rating(params.rating) : null;

        this.executors = params.executors;
        this.expiredStatus = params.expiredStatus;
        this.flags = new ComplaintFlagsEntry(params.flags);
        this.internalStatus = params.internalStatus;
        this.messageStationId = params.messageStationId;
        this.operator = params.operator ? new DictionaryEntry(params.operator) : null;
        this.responseNotificationAddress = params.responseNotificationAddress;
        this.sedNumber = params.sedNumber;
        this.tags = params.tags;
        this.rating = params.rating ? new Rating(params.rating) : null;

        this.meetingDate = DateService.getValidDate(params.meetingDate);
        this.videoChatId = params.videoChatId;
        this.meetingPerson = params.meetingPerson;
    }
}
